import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="search"
// Provides instant search with debouncing
export default class extends Controller {
  static targets = ["input", "results", "loading", "clear"]
  static values = {
    url: String,
    debounce: { type: Number, default: 250 },
    minChars: { type: Number, default: 2 }
  }

  connect() {
    this.timeout = null
    this.updateClearButton()
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  }

  search(event) {
    const query = this.inputTarget.value.trim()
    
    this.updateClearButton()

    // Clear previous timeout
    if (this.timeout) {
      clearTimeout(this.timeout)
    }

    // Don't search if query is too short
    if (query.length > 0 && query.length < this.minCharsValue) {
      return
    }

    // Debounce the search
    this.timeout = setTimeout(() => {
      this.performSearch(query)
    }, this.debounceValue)
  }

  async performSearch(query) {
    const url = new URL(this.urlValue || window.location.pathname, window.location.origin)
    
    if (query) {
      url.searchParams.set("q", query)
    } else {
      url.searchParams.delete("q")
    }

    // Preserve other params
    const currentParams = new URLSearchParams(window.location.search)
    for (const [key, value] of currentParams) {
      if (key !== "q" && key !== "page") {
        url.searchParams.set(key, value)
      }
    }

    // Show loading
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
    }

    try {
      const response = await fetch(url, {
        headers: {
          "Accept": "text/html",
          "Turbo-Frame": "results"
        }
      })

      if (response.ok) {
        // Update URL
        window.history.pushState({}, "", url)
        
        // Let Turbo handle the response if using frames
        // Otherwise, we'd parse and update the results target
        if (this.hasResultsTarget && !document.querySelector("turbo-frame#results")) {
          const html = await response.text()
          const parser = new DOMParser()
          const doc = parser.parseFromString(html, "text/html")
          const newResults = doc.querySelector("[data-search-target='results']") || 
                            doc.querySelector("#results") ||
                            doc.querySelector(".results")
          if (newResults) {
            this.resultsTarget.innerHTML = newResults.innerHTML
          }
        } else {
          // Using Turbo frames - let it handle the update
          Turbo.visit(url, { frame: "results" })
        }
      }
    } catch (error) {
      console.error("Search error:", error)
    } finally {
      if (this.hasLoadingTarget) {
        this.loadingTarget.classList.add("hidden")
      }
    }
  }

  clear(event) {
    event.preventDefault()
    this.inputTarget.value = ""
    this.updateClearButton()
    this.performSearch("")
  }

  updateClearButton() {
    if (this.hasClearTarget) {
      if (this.inputTarget.value.trim()) {
        this.clearTarget.classList.remove("hidden")
      } else {
        this.clearTarget.classList.add("hidden")
      }
    }
  }

  // Handle form submission
  submit(event) {
    event.preventDefault()
    const query = this.inputTarget.value.trim()
    this.performSearch(query)
  }
}
